import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { Drauu } from "drauu";

import {
  ANNOTATION_COLOR_HEX,
  BRUSH_SIZE_MULTIPLIER,
  NOTE_TEXT_HEX,
  useAnnotationStore,
  type AnnotationColor,
  type AnnotationTool,
  type BrushSize,
} from "@/stores/annotation-store";
import { useSlideStore } from "@/stores/slide-store";

// drauu is ~5KB so lazy-loading is more about consistency than necessity.
// Module-cache the import so future re-mounts skip the network round-trip.
let cachedDrauu: typeof import("drauu") | null = null;

async function ensureDrauu() {
  if (cachedDrauu) return cachedDrauu;
  cachedDrauu = await import("drauu");
  return cachedDrauu;
}

// Imperative handle for the presenter's "Clear current slide" button.
// The presenter doesn't write committed strokes back to the local
// annotation store (that would trigger drauu.load() and break per-stroke
// laser fade — see onChange handling below), so its store has no
// snapshot key for the current slide. When the user clicks Clear, the
// store's clearSlide() is a no-op for that key and the overlay's
// liveSnapshot subscription never fires. This handle gives the toolbar
// a direct path to wipe drauu locally. Viewers don't need this — their
// liveSnapshot was set by BC inbound, so clearSlide flipping it back to
// undefined triggers the combined effect normally.
export interface AnnotationOverlayHandle {
  clearCanvas: () => void;
}

export interface AnnotationOverlayProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  slideIndex: number;
  mode: "interactive" | "render-only";
  enabled: boolean;
  // Called with the full SVG dump after every user stroke commit. Parent
  // is expected to BC-broadcast to peer surfaces. Not called when the
  // overlay is in render-only mode or when drauu's "committed" event was
  // triggered by our own load() call (gated via applyingRemoteRef).
  onChange?: (svg: string) => void;
}

// Maps annotation tools to drauu modes + brush config. Marker = thin
// opaque stroke; highlighter = wide + semi-transparent (alpha 0.4) so
// underlying slide content remains visible; eraser = drauu's eraseLine
// which removes paths intersecting the eraser line; arrow = drauu's
// line mode with arrowEnd. Base sizes are scaled by the user's selected
// BrushSize multiplier (S/M/L/XL).
//
// `text` and `note` are NOT drauu-authored — their pointerdown is
// intercepted before drauu sees it (see the capture-phase listener in
// the overlay body). For safety the brush still gets set to something
// benign so an unintercepted event would degrade to a small stroke
// rather than crashing.
function brushConfigFor(
  tool: Exclude<AnnotationTool, null>,
  color: AnnotationColor,
  size: BrushSize,
): {
  mode: "draw" | "line" | "eraseLine";
  color: string;
  size: number;
  arrowEnd?: boolean;
} {
  const hex = ANNOTATION_COLOR_HEX[color];
  const mult = BRUSH_SIZE_MULTIPLIER[size];
  switch (tool) {
    case "draw":
      return { mode: "draw", color: hex, size: 12 * mult };
    case "highlight":
      return { mode: "draw", color: hexToRgba(hex, 0.4), size: 28 * mult };
    case "eraser":
      return { mode: "eraseLine", color: hex, size: 24 * mult };
    case "laser":
      // drauu has no native laser mode, so we use the regular draw mode
      // and add per-stroke fade in the committed handler below.
      return { mode: "draw", color: hex, size: 10 * mult };
    case "arrow":
      return {
        mode: "line",
        color: hex,
        size: 8 * mult,
        arrowEnd: true,
      };
    case "text":
    case "note":
    case "hand":
      // Custom tool — handled by the capture-phase pointerdown listener.
      // drauu mode is benign fallback only.
      return { mode: "draw", color: hex, size: 12 * mult };
  }
}

// SVG text element factory for the Text tool. Plain `<text>` positioned
// at the click point in viewBox coords.
// Builds the SVG <text> element for the Text tool. Font size follows
// the brushSize multiplier (S/M/L/XL) — consistent with marker /
// highlighter / etc. The drag rect's vbX/vbY position the text's
// top-left; rect height is no longer used (text would otherwise scale
// to fill the rect, which felt wildly oversized for the typical
// "circle a word" use case).
function buildTextElement(
  content: string,
  vbX: number,
  vbY: number,
  color: AnnotationColor,
  size: BrushSize,
): SVGTextElement {
  const fontSize = TEXT_BASE_FONT_SIZE * BRUSH_SIZE_MULTIPLIER[size];
  const text = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "text",
  );
  text.setAttribute("x", String(vbX));
  // Position by baseline; vbY is the rect top-left, so push down by
  // ~80% of the font size to land the visible glyphs inside the rect.
  text.setAttribute("y", String(vbY + fontSize * 0.8));
  text.setAttribute("font-family", "sans-serif");
  text.setAttribute("font-weight", "700");
  text.setAttribute("font-size", String(fontSize));
  text.setAttribute("fill", ANNOTATION_COLOR_HEX[color]);
  // Marker for the eraser's click-to-delete intercept. drauu's
  // eraseLine ignores non-path elements, so we hit-test these manually.
  text.setAttribute("data-annotation-type", "text");
  text.textContent = content;
  return text;
}

// Base font sizes in viewBox units. Multiplied by BRUSH_SIZE_MULTIPLIER
// to honor the toolbar's S/M/L/XL selector. Tuned so the M default
// reads at a normal "annotation caption" size on a 1920×1080 slide.
const TEXT_BASE_FONT_SIZE = 64;
const NOTE_BASE_FONT_SIZE = 56;

// Builds the SVG sticky-note for the Note tool. Drag rect bounds define
// the note's exact width × height. Background color follows the user's
// selected color; text color is the auto-contrast partner from
// NOTE_TEXT_HEX. Font size follows brushSize multiplier — same model
// as the Text tool, so rect size purely controls the note's footprint.
function buildNoteElement(
  content: string,
  vbX: number,
  vbY: number,
  vbWidth: number,
  vbHeight: number,
  color: AnnotationColor,
  size: BrushSize,
): SVGGElement {
  const fontSize = NOTE_BASE_FONT_SIZE * BRUSH_SIZE_MULTIPLIER[size];

  const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
  g.setAttribute("transform", `translate(${vbX}, ${vbY})`);
  // Marker for the eraser's click-to-delete intercept.
  g.setAttribute("data-annotation-type", "note");

  const rect = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "rect",
  );
  rect.setAttribute("x", "0");
  rect.setAttribute("y", "0");
  rect.setAttribute("width", String(vbWidth));
  rect.setAttribute("height", String(vbHeight));
  rect.setAttribute("fill", ANNOTATION_COLOR_HEX[color]);
  rect.setAttribute("rx", "4");
  rect.setAttribute(
    "style",
    "filter: drop-shadow(4px 6px 8px rgba(0, 0, 0, 0.25))",
  );
  g.appendChild(rect);

  const text = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "text",
  );
  text.setAttribute("x", String(vbWidth / 2));
  text.setAttribute("y", String(vbHeight / 2));
  text.setAttribute("text-anchor", "middle");
  text.setAttribute("dominant-baseline", "middle");
  text.setAttribute("font-family", "sans-serif");
  text.setAttribute("font-weight", "700");
  text.setAttribute("font-size", String(fontSize));
  text.setAttribute("fill", NOTE_TEXT_HEX[color]);
  text.textContent = content;
  g.appendChild(text);

  return g;
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const AnnotationOverlay = forwardRef<
  AnnotationOverlayHandle,
  AnnotationOverlayProps
>(function AnnotationOverlay(
  { iframeRef, slideIndex, mode, enabled, onChange },
  ref,
) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drauuRef = useRef<Drauu | null>(null);
  const onChangeRef = useRef(onChange);
  const slideIndexRef = useRef(slideIndex);
  // Set true while we're calling drauu.load()/clear() programmatically
  // so drauu's "committed" event doesn't BC-echo it back. drauu has no
  // source filter like tldraw, so we gate emit-path with this ref.
  const applyingRemoteRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const activeTool = useAnnotationStore((s) => s.activeTool);
  const color = useAnnotationStore((s) => s.color);
  const brushSize = useAnnotationStore((s) => s.brushSize);
  const saveSlideSnapshot = useAnnotationStore((s) => s.saveSlideSnapshot);

  // Drag-to-place state machine for Text + Note tools. Two phases:
  //   "dragging": user is holding the pointer down; render the preview
  //               rect outline from start to current position.
  //   "typing":   pointer released; render an <input> sized to the
  //               final rect for the user to type into. Blur/Enter
  //               commits an SVG element with those exact bounds.
  // All coords are wrapper-relative (CSS px). viewBox conversion runs
  // at commit time so any layout shift between drag and commit doesn't
  // desync the placed element.
  const [dragState, setDragState] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    kind: "text" | "note";
    phase: "dragging" | "typing";
  } | null>(null);
  const pendingInputRef = useRef<HTMLInputElement | null>(null);

  // Hand-tool element-move state. Cursor swaps from `grab` to
  // `grabbing` while a drag is in flight; rendering doesn't change
  // otherwise — the SVG element's x/y or transform updates in place.
  const [isMovingElement, setIsMovingElement] = useState(false);

  // Laser tool — each committed stroke schedules an opacity fade then
  // removes its SVG node. Implemented per-stroke (not deck-wide) so a
  // user gesturing several times in a row sees each laser line fade
  // independently, matching the Google Slides Laser UX. Tracks all
  // currently-fading strokes so we can cancel them on unmount / slide
  // nav / End Presentation.
  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);
  const laserTimersRef = useRef<Set<number>>(new Set());
  // Subscribe to the CURRENT slide's snapshot. Watching this drives the
  // store→drauu sync direction: BC peers writing to the store will trigger
  // an apply here if the change is for the slide we're viewing.
  const liveSnapshot = useAnnotationStore(
    (s) => s.snapshotsBySlide[slideIndex],
  );

  // SVG viewBox follows deck aspect ratio so strokes are stored in
  // slide-logical units (not screen pixels). BC peers at different
  // physical sizes render strokes at the same proportional position.
  const aspectRatio = useSlideStore((s) => s.presentationConfig.aspectRatio);
  const viewBox = aspectRatio === "4:3" ? "0 0 1440 1080" : "0 0 1920 1080";

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Anchor wrapper to iframe rect via ResizeObserver + scroll/resize
  // listeners. position:fixed so we don't depend on containing-block
  // positioning — works equally in PresenterDashboard and the editor's
  // createPortal Present overlay.
  //
  // BROWSER FULLSCREEN GOTCHA: the iframe lives inside ScaledSlideIframe,
  // which renders it at logical 1920×1080 + `transform: scale(N)`. The
  // iframe's intrinsic size never changes, only its transform. So:
  //  - ResizeObserver on the iframe alone does NOT fire when fullscreen
  //    rescales (transform changes don't trigger RO).
  //  - window.resize fires immediately on fullscreen entry — BEFORE
  //    ScaledSlideIframe's own RO recomputes scale + React commits the
  //    new transform — so a synchronous sync reads the STALE rect.
  // Fix: also observe iframe.parentElement (which DOES resize), listen
  // for `fullscreenchange`, and defer all syncs via rAF so they run
  // AFTER ScaledSlideIframe has re-rendered.
  useEffect(() => {
    const iframe = iframeRef.current;
    const wrapper = wrapperRef.current;
    if (!iframe || !wrapper) return;
    let rafId: number | null = null;
    const sync = () => {
      const rect = iframe.getBoundingClientRect();
      wrapper.style.left = `${rect.left}px`;
      wrapper.style.top = `${rect.top}px`;
      wrapper.style.width = `${rect.width}px`;
      wrapper.style.height = `${rect.height}px`;
    };
    const scheduleSync = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        sync();
      });
    };
    sync();
    const ro = new ResizeObserver(scheduleSync);
    ro.observe(iframe);
    if (iframe.parentElement) ro.observe(iframe.parentElement);
    window.addEventListener("resize", scheduleSync);
    window.addEventListener("scroll", scheduleSync, true);
    document.addEventListener("fullscreenchange", scheduleSync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("scroll", scheduleSync, true);
      document.removeEventListener("fullscreenchange", scheduleSync);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [iframeRef]);

  // Initialize drauu once the SVG element is mounted. Single mount/
  // unmount lifecycle — everything reactive happens through the effects
  // below that mutate drauu imperatively.
  useEffect(() => {
    let cancelled = false;
    let drauu: Drauu | null = null;
    // rAF-throttled emit token — drauu fires `changed` per pointer-move
    // during drawing, so we'd otherwise BC-broadcast 100+ times/sec for
    // a continuous stroke. One frame is plenty for the audience to see
    // smooth growth.
    let pendingEmitRaf: number | null = null;
    (async () => {
      const mod = await ensureDrauu();
      if (cancelled || !svgRef.current) return;
      drauu = mod.createDrauu({
        el: svgRef.current,
        brush: { mode: "draw", color: "#e03131", size: 12 },
        acceptsInputTypes: ["mouse", "touch", "pen"],
      });
      drauuRef.current = drauu;

      // Live-stream strokes via drauu's `changed` event. Fires on every
      // SVG mutation (mid-stroke pointer-move included), so the audience
      // sees the line growing in real time instead of waiting for
      // pointerup. applyingRemoteRef gates this so programmatic load()
      // calls don't echo back over BC.
      const emitCurrentSvg = () => {
        pendingEmitRaf = null;
        if (applyingRemoteRef.current) return;
        if (mode !== "interactive") return;
        const svg = drauu!.el?.innerHTML ?? "";
        onChangeRef.current?.(svg);
      };
      const scheduleEmit = () => {
        if (pendingEmitRaf !== null) return;
        pendingEmitRaf = requestAnimationFrame(emitCurrentSvg);
      };
      drauu.on("changed", scheduleEmit);

      drauu.on("committed", (node) => {
        // Coalesce: the canonical final-state send happens here, so
        // cancel any throttled mid-stroke emit still pending.
        if (pendingEmitRaf !== null) {
          cancelAnimationFrame(pendingEmitRaf);
          pendingEmitRaf = null;
        }
        if (applyingRemoteRef.current) return;
        if (mode !== "interactive") return;
        const svg = drauu!.el?.innerHTML ?? "";
        onChangeRef.current?.(svg);

        // Laser tool — per-stroke fade. ~500ms total lifetime: 100ms
        // visible, then 400ms opacity transition. After fade, remove the
        // node from drauu's SVG and BC-send the now-shorter SVG so peers
        // stay in sync. Other tools leave strokes persistent.
        if (activeToolRef.current !== "laser" || !node) return;
        const path = node as SVGElement;
        const fadeTimerId = window.setTimeout(() => {
          laserTimersRef.current.delete(fadeTimerId);
          path.style.transition = "opacity 0.4s ease-out";
          path.style.opacity = "0";
          const removeTimerId = window.setTimeout(() => {
            laserTimersRef.current.delete(removeTimerId);
            // Remove the path. drauu's internal op stack still holds the
            // operation so undo could mis-fire — laser strokes aren't
            // expected to be undoable, so we accept this trade-off.
            path.remove();
            onChangeRef.current?.(drauu!.el?.innerHTML ?? "");
          }, 400);
          laserTimersRef.current.add(removeTimerId);
        }, 300);
        laserTimersRef.current.add(fadeTimerId);
      });

      setIsReady(true);
    })();
    return () => {
      cancelled = true;
      if (pendingEmitRaf !== null) {
        cancelAnimationFrame(pendingEmitRaf);
        pendingEmitRaf = null;
      }
      for (const id of laserTimersRef.current) window.clearTimeout(id);
      laserTimersRef.current.clear();
      if (drauu) drauu.unmount();
      drauuRef.current = null;
      setIsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture-phase pointerdown listener for the Text + Note tools. Fires
  // BEFORE drauu's own pointerdown handler so we can stop drauu from
  // starting a stroke when activeTool is text/note. Reads activeTool
  // from the ref so this effect doesn't have to re-bind when the tool
  // changes.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !isReady) return;
    if (mode !== "interactive") return;
    const onPointerDownCapture = (e: PointerEvent) => {
      const tool = activeToolRef.current;

      // Hand branch — drag-to-move for tagged annotation elements
      // (text + note). Always intercepts so drauu doesn't start a
      // stroke on empty clicks; if the click missed a tagged element
      // we just no-op.
      if (tool === "hand") {
        e.preventDefault();
        e.stopImmediatePropagation();
        const target = e.target as Element | null;
        const tagged = target?.closest?.(
          "[data-annotation-type]",
        ) as SVGElement | null;
        const drauu = drauuRef.current;
        if (!tagged || !drauu?.el || !drauu.el.contains(tagged)) return;

        const startVb = screenToViewBox(e.clientX, e.clientY);
        // Read element's current viewBox position. Text uses x/y
        // attributes (number); note <g> uses transform="translate(x, y)".
        let origX = 0;
        let origY = 0;
        const tagName = tagged.tagName.toLowerCase();
        if (tagName === "text") {
          origX = parseFloat(tagged.getAttribute("x") ?? "0");
          origY = parseFloat(tagged.getAttribute("y") ?? "0");
        } else if (tagName === "g") {
          const transform = tagged.getAttribute("transform") ?? "";
          const match = transform.match(
            /translate\(\s*(-?[\d.]+)[ ,]+(-?[\d.]+)\s*\)/,
          );
          if (match) {
            origX = parseFloat(match[1]);
            origY = parseFloat(match[2]);
          }
        }

        setIsMovingElement(true);

        // rAF-throttled BC emit during the move so the audience sees
        // smooth dragging at most once per frame (60 Hz cap, not 100+
        // per pointer-move).
        let moveRaf: number | null = null;
        const scheduleEmit = () => {
          if (moveRaf !== null) return;
          moveRaf = requestAnimationFrame(() => {
            moveRaf = null;
            if (mode !== "interactive") return;
            const svg = drauu.el?.innerHTML ?? "";
            onChangeRef.current?.(svg);
          });
        };

        const onMove = (moveEvent: PointerEvent) => {
          const currentVb = screenToViewBox(
            moveEvent.clientX,
            moveEvent.clientY,
          );
          const newX = origX + (currentVb.x - startVb.x);
          const newY = origY + (currentVb.y - startVb.y);
          if (tagName === "text") {
            tagged.setAttribute("x", String(newX));
            tagged.setAttribute("y", String(newY));
          } else if (tagName === "g") {
            tagged.setAttribute(
              "transform",
              `translate(${newX}, ${newY})`,
            );
          }
          scheduleEmit();
        };

        const onUp = () => {
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          if (moveRaf !== null) {
            cancelAnimationFrame(moveRaf);
            moveRaf = null;
          }
          setIsMovingElement(false);
          // Final emit so the audience sees the resting position
          // even if the last move was throttled out.
          if (mode === "interactive") {
            onChangeRef.current?.(drauu.el?.innerHTML ?? "");
          }
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return;
      }

      // Eraser branch — drauu only erases its own paths, so we add
      // click-to-delete for text/note elements we appended manually.
      // If the click landed on a tagged ancestor, remove it and BC-sync.
      // Otherwise fall through so drauu's eraseLine handles strokes.
      if (tool === "eraser") {
        const target = e.target as Element | null;
        const tagged = target?.closest?.("[data-annotation-type]");
        const drauu = drauuRef.current;
        if (
          tagged &&
          drauu?.el &&
          drauu.el.contains(tagged) &&
          tagged !== drauu.el
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
          tagged.remove();
          if (mode === "interactive") {
            onChangeRef.current?.(drauu.el.innerHTML ?? "");
          }
        }
        return;
      }

      // Text / Note branch — drag-to-place. Begin tracking pointer
      // until pointerup, then transition to typing phase.
      if (tool !== "text" && tool !== "note") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      const startX = e.clientX - wrapperRect.left;
      const startY = e.clientY - wrapperRect.top;
      setDragState({
        startX,
        startY,
        currentX: startX,
        currentY: startY,
        kind: tool,
        phase: "dragging",
      });
      const onMove = (moveEvent: PointerEvent) => {
        const r = wrapperRef.current?.getBoundingClientRect();
        if (!r) return;
        setDragState((prev) =>
          prev
            ? {
                ...prev,
                currentX: moveEvent.clientX - r.left,
                currentY: moveEvent.clientY - r.top,
              }
            : null,
        );
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        // Transition to typing — the preview rect becomes the input
        // bounds. If the user just clicked without dragging, the rect
        // collapses to a point; we'll inflate to a sensible default at
        // render time so the input is still usable.
        setDragState((prev) =>
          prev ? { ...prev, phase: "typing" } : null,
        );
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    };
    svg.addEventListener("pointerdown", onPointerDownCapture, {
      capture: true,
    });
    return () => {
      svg.removeEventListener("pointerdown", onPointerDownCapture, {
        capture: true,
      });
    };
  }, [isReady, mode]);

  // Auto-focus the input when the drag transitions to the typing phase.
  useEffect(() => {
    if (dragState?.phase === "typing") pendingInputRef.current?.focus();
  }, [dragState?.phase]);

  // Convert viewport coords (from a click event) to the SVG's viewBox
  // coordinate space. Uses getScreenCTM inverse — handles scale,
  // letterbox, and any transform that's been applied to the SVG.
  const screenToViewBox = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const point = new DOMPoint(clientX, clientY).matrixTransform(
        ctm.inverse(),
      );
      return { x: point.x, y: point.y };
    },
    [],
  );

  // Commit the dragged text/note as an SVG element appended to drauu.el.
  // The drag rect's bounds drive the element's position + size.
  // BC sync runs explicitly (manual DOM appends don't fire drauu's
  // `changed` event).
  const commitPendingText = useCallback(
    (value: string) => {
      const drauu = drauuRef.current;
      const drag = dragState;
      if (!drauu || !drag) {
        setDragState(null);
        return;
      }
      const trimmed = value.trim();
      if (!trimmed) {
        setDragState(null);
        return;
      }
      // Normalize rect bounds (drag may be right-to-left or
      // bottom-to-top). Inflate too-small rects (click without drag)
      // to a sensible default so the element is still legible.
      const left = Math.min(drag.startX, drag.currentX);
      const top = Math.min(drag.startY, drag.currentY);
      const right = Math.max(drag.startX, drag.currentX);
      const bottom = Math.max(drag.startY, drag.currentY);
      const wrapper = wrapperRef.current;
      const wrapperRect = wrapper?.getBoundingClientRect();
      const wrapperOriginLeft = wrapperRect?.left ?? 0;
      const wrapperOriginTop = wrapperRect?.top ?? 0;
      const minPx = drag.kind === "note" ? 120 : 40;
      const adjRight = right - left < minPx ? left + minPx : right;
      const adjBottom = bottom - top < minPx ? top + minPx : bottom;
      // Wrapper-relative px → viewport px → viewBox coords.
      const vbTopLeft = screenToViewBox(
        wrapperOriginLeft + left,
        wrapperOriginTop + top,
      );
      const vbBotRight = screenToViewBox(
        wrapperOriginLeft + adjRight,
        wrapperOriginTop + adjBottom,
      );
      const vbWidth = vbBotRight.x - vbTopLeft.x;
      const vbHeight = vbBotRight.y - vbTopLeft.y;
      const el =
        drag.kind === "note"
          ? buildNoteElement(
              trimmed,
              vbTopLeft.x,
              vbTopLeft.y,
              vbWidth,
              vbHeight,
              color,
              brushSize,
            )
          : buildTextElement(
              trimmed,
              vbTopLeft.x,
              vbTopLeft.y,
              color,
              brushSize,
            );
      drauu.el?.appendChild(el);
      // Trigger BC sync (manual appends don't fire drauu's "changed").
      if (mode === "interactive") {
        onChangeRef.current?.(drauu.el?.innerHTML ?? "");
      }
      setDragState(null);
    },
    [dragState, screenToViewBox, color, brushSize, mode],
  );

  // Combined slide-change + snapshot-sync effect.
  //
  // Two concerns funnel into the same write to drauu:
  //  1. slideIndex changed — save outgoing slide's drauu state to the
  //     store, then load incoming slide's snapshot.
  //  2. liveSnapshot changed (BC peer wrote to the store, or local
  //     toolbar Clear) — apply to drauu directly.
  //
  // Both paths gate via applyingRemoteRef so drauu's "committed" event
  // doesn't echo back via onChange.
  useEffect(() => {
    const drauu = drauuRef.current;
    if (!drauu || !isReady) {
      slideIndexRef.current = slideIndex;
      return;
    }
    const prevIndex = slideIndexRef.current;
    if (prevIndex !== slideIndex) {
      // Cancel pending laser fades — they'd otherwise try to remove
      // paths from the NEXT slide's loaded SVG.
      for (const id of laserTimersRef.current) window.clearTimeout(id);
      laserTimersRef.current.clear();
      // Cancel any text/note placement in progress — the drag/typing
      // position belongs to the previous slide.
      setDragState(null);
      const outgoing = drauu.el?.innerHTML ?? "";
      if (outgoing) saveSlideSnapshot(prevIndex, outgoing);
    }
    applyingRemoteRef.current = true;
    if (liveSnapshot !== undefined && liveSnapshot !== "") {
      drauu.load(liveSnapshot);
    } else {
      drauu.clear();
    }
    queueMicrotask(() => {
      applyingRemoteRef.current = false;
    });
    slideIndexRef.current = slideIndex;
  }, [slideIndex, liveSnapshot, isReady, saveSlideSnapshot]);

  // Tool + color + size sync. Re-derived whenever store state changes
  // so the toolbar feels instant.
  useEffect(() => {
    const drauu = drauuRef.current;
    if (!drauu || !isReady) return;
    if (activeTool === null) return;
    const cfg = brushConfigFor(activeTool, color, brushSize);
    drauu.mode = cfg.mode;
    drauu.brush = {
      mode: cfg.mode,
      color: cfg.color,
      size: cfg.size,
      // Only the arrow tool sets this; drauu ignores it for non-line
      // modes per its brush spec.
      ...(cfg.arrowEnd !== undefined && { arrowEnd: cfg.arrowEnd }),
    };
  }, [activeTool, color, brushSize, isReady]);

  // Switching tool away from text/note should dismiss any pending
  // drag/input. Same applies to slide nav (handled in the combined
  // effect below).
  useEffect(() => {
    if (activeTool !== "text" && activeTool !== "note") {
      setDragState(null);
    }
  }, [activeTool]);

  useImperativeHandle(
    ref,
    () => ({
      clearCanvas: () => {
        const drauu = drauuRef.current;
        if (!drauu) return;
        // Cancel any in-flight laser fade — they'd otherwise remove
        // nodes that no longer exist after drauu.clear().
        for (const id of laserTimersRef.current) window.clearTimeout(id);
        laserTimersRef.current.clear();
        // Gate the "committed" event from echoing this clear via BC —
        // the caller (umbrella) is responsible for broadcasting
        // annotation-clear through the hook separately.
        applyingRemoteRef.current = true;
        drauu.clear();
        queueMicrotask(() => {
          applyingRemoteRef.current = false;
        });
      },
    }),
    [],
  );

  const effectivePointerEvents =
    mode === "interactive" && enabled ? "auto" : "none";

  // Cursor feedback per tool. Eraser uses the Lucide eraser shape as an
  // inline SVG data-URL so it visually matches the toolbar icon —
  // hotspot at (5, 19) which is the eraser tip in the 24×24 viewBox.
  // White fill + dark stroke + soft drop shadow for readable contrast
  // on any slide background.
  const cursorForTool = (() => {
    if (mode !== "interactive" || !enabled) return undefined;
    switch (activeTool) {
      case "eraser":
        return `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='white' stroke='%231d1d1d' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21'/><path d='M22 21H7'/><path d='m5 11 9 9'/></svg>") 4 14, auto`;
      case "hand":
        return isMovingElement ? "grabbing" : "grab";
      case "text":
      case "note":
        return "text";
      case "draw":
      case "highlight":
      case "arrow":
      case "laser":
        return "crosshair";
      default:
        return undefined;
    }
  })();

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        zIndex: 10,
        pointerEvents: effectivePointerEvents,
        touchAction:
          mode === "interactive" && enabled ? "none" : undefined,
        cursor: cursorForTool,
      }}
    >
      <svg
        ref={svgRef}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={viewBox}
        preserveAspectRatio="none"
        style={{ width: "100%", height: "100%", display: "block" }}
      />
      {dragState &&
        (() => {
          // Normalize bounds + inflate small-rect (click-only) to a
          // sensible default so the input is always visible.
          const left = Math.min(dragState.startX, dragState.currentX);
          const top = Math.min(dragState.startY, dragState.currentY);
          const rawRight = Math.max(dragState.startX, dragState.currentX);
          const rawBottom = Math.max(dragState.startY, dragState.currentY);
          const minPx = dragState.kind === "note" ? 120 : 40;
          const width =
            dragState.phase === "typing"
              ? Math.max(rawRight - left, minPx)
              : Math.max(rawRight - left, 1);
          const height =
            dragState.phase === "typing"
              ? Math.max(rawBottom - top, minPx)
              : Math.max(rawBottom - top, 1);
          const isNote = dragState.kind === "note";

          if (dragState.phase === "dragging") {
            // Preview rect — text uses dashed blue outline; note
            // previews with the selected color at 60% opacity so the
            // drag matches the committed result.
            return (
              <div
                style={{
                  position: "absolute",
                  left: `${left}px`,
                  top: `${top}px`,
                  width: `${width}px`,
                  height: `${height}px`,
                  border: isNote
                    ? "2px dashed rgba(0, 0, 0, 0.3)"
                    : "2px solid #3b82f6",
                  background: isNote
                    ? `${ANNOTATION_COLOR_HEX[color]}99`
                    : "transparent",
                  borderRadius: isNote ? "4px" : 0,
                  pointerEvents: "none",
                  zIndex: 11,
                }}
              />
            );
          }

          // Typing phase — input sized to rect bounds. Font size is
          // WYSIWYG with the committed SVG: viewBox font-size scaled
          // by the screen/viewBox ratio so the typed letters look the
          // same size as the committed result.
          const wrapperRect = wrapperRef.current?.getBoundingClientRect();
          const viewBoxH = 1080;
          const screenScale = (wrapperRect?.height ?? viewBoxH) / viewBoxH;
          const vbFontSize =
            (isNote ? NOTE_BASE_FONT_SIZE : TEXT_BASE_FONT_SIZE) *
            BRUSH_SIZE_MULTIPLIER[brushSize];
          const inputFontSize = Math.max(12, vbFontSize * screenScale);
          return (
            <input
              ref={pendingInputRef}
              type="text"
              autoFocus
              placeholder={isNote ? "Note…" : "Text…"}
              onBlur={(e) => commitPendingText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitPendingText(e.currentTarget.value);
                } else if (e.key === "Escape") {
                  setDragState(null);
                }
                // Stop bubbling so global keyboard handlers (slide
                // nav, Esc-exits-present, etc.) don't fire while
                // typing.
                e.stopPropagation();
              }}
              style={{
                position: "absolute",
                left: `${left}px`,
                top: `${top}px`,
                width: `${width}px`,
                height: `${height}px`,
                padding: isNote ? "8px 12px" : "0 4px",
                fontSize: `${inputFontSize}px`,
                fontFamily: "sans-serif",
                fontWeight: 700,
                textAlign: isNote ? "center" : "left",
                color: isNote
                  ? NOTE_TEXT_HEX[color]
                  : ANNOTATION_COLOR_HEX[color],
                background: isNote
                  ? ANNOTATION_COLOR_HEX[color]
                  : "transparent",
                border: isNote ? "none" : "2px solid #3b82f6",
                borderRadius: isNote ? "4px" : 0,
                boxShadow: isNote
                  ? "4px 6px 8px rgba(0, 0, 0, 0.25)"
                  : "none",
                outline: "none",
                zIndex: 11,
                boxSizing: "border-box",
              }}
            />
          );
        })()}
    </div>
  );
});
